/**
 * usePermits Hook
 * Manages permit CRUD operations with loading and error states
 */

import { useState, useEffect, useCallback } from 'react';
import { permitApi } from '../services/api';
import type { Permit, CreatePermitRequest } from '../types/job';

interface UsePermitsResult {
  permits: Permit[];
  isLoading: boolean;
  error: string | null;
  refreshPermits: () => Promise<void>;
  createPermit: (data: CreatePermitRequest) => Promise<Permit | null>;
  deletePermit: (permitId: string) => Promise<boolean>;
}

export function usePermits(jobId: string | undefined): UsePermitsResult {
  const [permits, setPermits] = useState<Permit[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch all permits for the job
   */
  const fetchPermits = useCallback(async () => {
    if (!jobId) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const data = await permitApi.getPermits(jobId);
      setPermits(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch permits';
      setError(errorMessage);
      console.error('Error fetching permits:', err);
    } finally {
      setIsLoading(false);
    }
  }, [jobId]);

  /**
   * Load permits on mount or when jobId changes
   */
  useEffect(() => {
    fetchPermits();
  }, [fetchPermits]);

  /**
   * Refresh permits
   */
  const refreshPermits = useCallback(async () => {
    await fetchPermits();
  }, [fetchPermits]);

  /**
   * Create a new permit
   */
  const createPermit = useCallback(
    async (data: CreatePermitRequest): Promise<Permit | null> => {
      if (!jobId) return null;

      setIsLoading(true);
      setError(null);

      try {
        const newPermit = await permitApi.createPermit(jobId, data);
        setPermits((prev) => [...prev, newPermit]);
        return newPermit;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to create permit';
        setError(errorMessage);
        console.error('Error creating permit:', err);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [jobId]
  );

  /**
   * Delete a permit
   */
  const deletePermit = useCallback(
    async (permitId: string): Promise<boolean> => {
      if (!jobId) return false;

      setIsLoading(true);
      setError(null);

      try {
        await permitApi.deletePermit(jobId, permitId);
        setPermits((prev) => prev.filter((permit) => permit.id !== permitId));
        return true;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to delete permit';
        setError(errorMessage);
        console.error('Error deleting permit:', err);
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [jobId]
  );

  return {
    permits,
    isLoading,
    error,
    refreshPermits,
    createPermit,
    deletePermit,
  };
}
